import { Divider, FormControlLabel, Radio, RadioGroup, Stack } from '@mui/material';

import {
  OrganizationAutocompleteControl,
  Text,
  isTruthy,
  useFormFieldState,
  useLoggedInAuthContext,
} from 'common';
import { Flow } from 'vivaa-api';

import { TextFieldControl } from '../../../../components/Control/TextField';
import { AgentCategorySelect } from '../../Details/Metadata/CategorySelect';
import { FlowTagFields } from '../../Details/TemplatePicker/Fields/FlowTagFields';
import {
  AgentIdControl,
  type AgentIdControlVariant,
  CREATE_AGENT_ID,
} from '../../Flows/Controls/AgentIdControl';
import { FlowKind, FlowKindContent } from '../../Flows/flowContent';

interface NewFlowDialogFieldsProps {
  selectedAgentId?: string;
  onAgentChange: (agentId: string | undefined) => void;
  agentIdControlVariant?: AgentIdControlVariant;
  showPricingMode?: boolean;
}

export function NewFlowDialogFields(props: NewFlowDialogFieldsProps) {
  const { selectedAgentId, onAgentChange, agentIdControlVariant, showPricingMode = false } = props;
  const { organizationsAreVisible } = useLoggedInAuthContext();
  const content = FlowKindContent[FlowKind.Base];

  const { value: organizationId } = useFormFieldState<string | undefined>({
    name: 'agent.organizationId',
  });
  const {
    value: agentName,
    setValue: setAgentName,
    errorMessage: agentNameErrorMessage,
  } = useFormFieldState<string | undefined>({ name: 'agent.name' });
  const { setValue: setAgentId } = useFormFieldState<string | undefined>({ name: 'agentId' });
  const { value: pricingMode, setValue: setPricingMode } = useFormFieldState<
    Flow.FlowPricingMode | undefined
  >({ name: 'pricingMode' });

  return (
    <Stack gap={2}>
      <TextFieldControl
        name="name"
        label={content.metadataFields.name.label}
        helperText={content.metadataFields.name.helperText}
        autoFocus
      />
      <FlowTagFields kind={FlowKind.Base} />
      {organizationsAreVisible && (
        <OrganizationAutocompleteControl
          name="agent.organizationId"
          label="Organization"
          onChange={async () => {
            if (selectedAgentId !== CREATE_AGENT_ID) {
              await setAgentId(undefined);
              onAgentChange(undefined);
            }
          }}
        />
      )}
      <AgentIdControl
        name="agentId"
        label="Agent"
        onAgentChange={async ({ agentId, agentName }) => {
          await setAgentName(agentName);
          onAgentChange(agentId);
        }}
        variant={agentIdControlVariant}
        agentName={agentName}
        agentNameErrorMessage={agentNameErrorMessage}
        organizationId={organizationsAreVisible ? organizationId : undefined}
        disabled={organizationsAreVisible && !isTruthy(organizationId)}
      />
      {selectedAgentId === CREATE_AGENT_ID && (
        <AgentCategorySelect fieldName="agent.category" label="Category" />
      )}
      {showPricingMode && (
        <>
          <Divider />
          <Stack gap={1}>
            <Text variant="heading4">Admin Only</Text>
            <Text variant="body2">Pricing Mode</Text>
            <RadioGroup
              aria-label="Pricing Mode"
              value={pricingMode ?? Flow.FlowPricingMode.Block}
              onChange={(event) => {
                void setPricingMode(event.target.value as Flow.FlowPricingMode);
              }}
            >
              <FormControlLabel
                value={Flow.FlowPricingMode.Block}
                control={<Radio />}
                label="Block"
              />
              <FormControlLabel
                value={Flow.FlowPricingMode.Task}
                control={<Radio />}
                label="Task"
              />
            </RadioGroup>
          </Stack>
        </>
      )}
    </Stack>
  );
}
